import React, { createContext, useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import Toast from '../components/Toast';
import { options } from '../helpers/toastOptions';
import api from '../services/api';

const PickupContext = createContext({});

const PickupProvider = ({ children }) => {
  const [branchParameterizationId, setBranchParameterizationId] = useState(0);
  const [
    branchWorkflowParameterizationId,
    setBranchWorkflowParameterizationId
  ] = useState(0);
  const [enablePreCollection, setEnablePreCollection] = useState(true);
  const [acceptDeclaration, setAcceptDeclaration] = useState(true);
  const [enableCheckList, setEnableCheckList] = useState(true);
  const [enableDisclaimerForDamages, setEnableDisclaimerForDamages] =
    useState(true);
  const [customerAcceptGuidelines, setCustomerAcceptGuidelines] =
    useState(false);

  const cancelDefaultPickup = useCallback(async ({ data }) => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/pickup/default/cancel`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response;
  }, []);

  const createDefaultPickup = useCallback(async ({ data }) => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v2/pickup/default/create`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    const { protocol } = response.data.data;

    return protocol;
  }, []);

  const cancelReversePickup = useCallback(async ({ data }) => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/pickup/reverse/cancel`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response;
  }, []);

  const createReversePickup = useCallback(async ({ data }) => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v2/pickup/reverse/create`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    const { protocol } = response.data.data;

    return protocol;
  }, []);

  const listUserLatestPickupAddress = useCallback(async () => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.get(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/pickup/latest-pickup-addresses`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    const { pickupAddressList, pickupContactList } = response.data.data;

    return { pickupAddressList, pickupContactList };
  }, []);

  const extendPickupDate = useCallback(async ({ data }) => {
    try {
      const token = localStorage.getItem('@Braspress:token');

      const response = await api.post(
        `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/extension`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      return response;
    } catch (error) {
      if (error?.response?.status === 400) {
        toast(
          <Toast
            content={error?.response?.data?.title}
            title="Atenção!"
            type="error"
          />,
          options
        );
      } else {
        toast(
          <Toast
            title="Atenção!"
            content="Falha ao prorrogar coleta, tente novamente mais tarde."
            type="error"
          />,
          options
        );
      }

      return error;
    }
  }, []);

  const extendPickupNoInvoice = useCallback(async ({ data }) => {
    try {
      const token = localStorage.getItem('@Braspress:token');

      const response = await api.post(
        `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/extension/no-invoice`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      return response;
    } catch (error) {
      if (error?.response?.status === 400) {
        toast(
          <Toast
            content={error?.response?.data?.title}
            title="Atenção!"
            type="error"
          />,
          options
        );
      } else {
        toast(
          <Toast
            title="Atenção!"
            content="Falha ao prorrogar coleta, tente novamente mais tarde."
            type="error"
          />,
          options
        );
      }

      return error;
    }
  }, []);

  const cancelInvoice = useCallback(async ({ data }) => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/pickup/cancel-invoices`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response;
  }, []);

  const cancelNoInvoice = useCallback(async ({ data }) => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/pickup/cancel-pickup`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response;
  }, []);

  const uploadTaxInvoice = useCallback(async ({ data }) => {
    const token = localStorage.getItem('@Braspress:token');

    const formData = new FormData();

    formData.append('InvoiceFile', data.file);
    formData.append('Number', data.number);
    formData.append('DateIssue', data.dateIssue);
    !!data.taxQuantity && formData.append('taxQuantity', data.taxQuantity);

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/pickup/invoice-upload/${data.pickupId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response;
  }, []);

  const uploadTaxDocument = useCallback(async ({ data }) => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/pickup/tax-document-upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response;
  }, []);

  const readTaxDocument = useCallback(async ({ file }) => {
    const token = localStorage.getItem('@Braspress:token');

    const formData = new FormData();

    formData.append('File', file);

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/xml/read-nfe`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response;
  }, []);

  const generateInvoiceTag = useCallback(async ({ data }) => {
    const token = localStorage.getItem('@Braspress:token');

    const response = await api.post(
      `${process.env.REACT_APP_URL_MS_PICKUP}/api/v1/ticket`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response;
  }, []);

  const contextValues = {
    branchParameterizationId,
    setBranchParameterizationId,
    branchWorkflowParameterizationId,
    setBranchWorkflowParameterizationId,
    enablePreCollection,
    setEnablePreCollection,
    acceptDeclaration,
    setAcceptDeclaration,
    enableCheckList,
    setEnableCheckList,
    enableDisclaimerForDamages,
    setEnableDisclaimerForDamages,
    customerAcceptGuidelines,
    setCustomerAcceptGuidelines,
    cancelDefaultPickup,
    createDefaultPickup,
    cancelReversePickup,
    createReversePickup,
    listUserLatestPickupAddress,
    extendPickupDate,
    extendPickupNoInvoice,
    cancelInvoice,
    cancelNoInvoice,
    uploadTaxInvoice,
    uploadTaxDocument,
    readTaxDocument,
    generateInvoiceTag
  };

  return (
    <PickupContext.Provider value={contextValues}>
      {children}
    </PickupContext.Provider>
  );
};

function usePickup() {
  const context = useContext(PickupContext);

  if (!context) {
    throw new Error('usePickup must be used within an PickupProvider');
  }

  return context;
}

export { PickupProvider, usePickup };
